import type { GuessResponse, ReportReason, RoundTodayResponse, Sport } from '@geosport/shared';

export interface GuessInput {
  progressToken: string;
  position: number;
  lat: number;
  lng: number;
  year: number;
}

export interface ReportInput {
  progressToken: string;
  position: number;
  reason: ReportReason;
  note?: string;
}

/** What the game needs from a backend (PRD §7): today's round, practice rounds, and scoring. */
export interface GameBackend {
  today(): Promise<RoundTodayResponse>;
  /** Replay a past day (practice, never ranked). */
  archive(date: string): Promise<RoundTodayResponse>;
  /** Five random past questions, optionally from one sport (practice). */
  random(sport: Sport | null): Promise<RoundTodayResponse>;
  /** A themed special by slug (practice). */
  special(slug: string): Promise<RoundTodayResponse>;
  guess(input: GuessInput): Promise<GuessResponse>;
  /** "Report a problem" with a question the player has answered. */
  report(input: ReportInput): Promise<void>;
}

let cached: Promise<GameBackend> | null = null;

/**
 * VITE_BACKEND=local plays against in-browser fixtures (M1 / demos / tests). Anything else uses
 * the Worker API. The comparison is against a build-time constant so the fixture bundle (which
 * contains answers) is dropped from production builds.
 */
export function getBackend(): Promise<GameBackend> {
  if (!cached) {
    cached =
      import.meta.env.VITE_BACKEND === 'local'
        ? import('./localBackend').then((m) => m.localBackend)
        : import('./apiBackend').then((m) => m.apiBackend);
  }
  return cached;
}
